import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { JmPayApi } from 'api';
import type { JmPay } from 'api/methods/JmPayApi';

/**
 * [JmPay] Query Key 생성
 * --
 */
export const jmpayQueryKeys = createQueryKeys('jm_pay', {
  getJmPay: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getJmPays: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [JmPay] 제이엠페이 전체 조회
 * --
 */
export const useGetJmPays = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: jmpayQueryKeys.getJmPays(params).queryKey,
    queryFn: () => JmPayApi.getJmPays(params),
    ...options,
  });

  return data;
};

/**
 * [JmPay] 제이엠페이 상세 조회
 * --
 */
export const useGetJmPay = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: jmpayQueryKeys.getJmPay(idx, params).queryKey,
    queryFn: () => JmPayApi.getJmPay(idx, params),
  });

  return data;
};

/**
 * [JmPay] 제이엠페이 생성
 * --
 */
export const useCreateJmPay = () => {
  const { mutateAsync: createJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.createJmPay(variables),
  });

  return { createJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 수정
 * --
 */
export const useUpdateJmPay = () => {
  const { mutateAsync: updateJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.updateJmPay(variables),
  });

  return { updateJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 삭제
 * --
 */
export const useDeleteJmPay = () => {
  const { mutateAsync: deleteJmPay, ...result } = useMutation({
    mutationFn: (variables: number) => JmPayApi.deleteJmPay(variables),
  });

  return { deleteJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 자동 충전
 * --
 */
export const useAutoChargeJmPay = () => {
  const { mutateAsync: autoChargeJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.autoChargeJmPay(variables),
  });

  return { autoChargeJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 수동 충전
 * --
 */
export const useManualChargeJmPay = () => {
  const { mutateAsync: manualChargeJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.manualChargeJmPay(variables),
  });

  return { manualChargeJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 충전 취소
 * --
 */
export const useCancelChargeJmPay = () => {
  const { mutateAsync: cancelChargeJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.cancelChargeJmPay(variables),
  });

  return { cancelChargeJmPay, ...result };
};

/**
 * [JmPay] 제이엠페이 충전 환불
 * --
 */
export const useRefundChargeJmPay = () => {
  const { mutateAsync: refundChargeJmPay, ...result } = useMutation({
    mutationFn: (variables: JmPay) => JmPayApi.refundChargeJmPay(variables),
  });

  return { refundChargeJmPay, ...result };
};
